import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button, Title } from "lola-framework-ui-test";
import { FloatingField } from "./FloatingField";
import { useStore } from "../store/globalState";
import { CloseLight } from "../assets/navIcons";

export interface CvvPrepareGateProps {
  last4: string;
  /** Card network (VISA, MASTERCARD, …) — shown UPPERCASE for identification. */
  brand?: string;
  loading?: boolean;
  error?: string | null;
  onSubmit: (cvv: string) => void;
  onClose: () => void;
}

/**
 * Inline CVV re-entry when funding rail prepare reports `needs_cvv`.
 */
export const CvvPrepareGate: React.FC<CvvPrepareGateProps> = ({
  last4,
  brand,
  loading,
  error,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { bodyCopy, backgroundBtn, textColorBtn } = theme.whiteView;
  const fieldColors = {
    background: theme.whiteView.background,
    title: theme.whiteView.title,
    bodyCopy,
    inactiveColor: theme.whiteView.subtitile,
    errorColor: theme.whiteView.errorColor,
  };
  const [cvv, setCvv] = useState("");
  const valid = /^\d{3,4}$/.test(cvv);
  const brandLabel = (brand || "").trim().toUpperCase();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || loading) return;
    onSubmit(cvv);
  };

  return (
    <form className="cvv-prepare-gate" onSubmit={handleSubmit}>
      <header className="payout-details-header">
        <span className="payout-header-action" aria-hidden="true" />
        <Title
          title={t("transfer.prepare.cvvTitle")}
          align="center"
          color={bodyCopy}
        />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label={t("common.cancel")}
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>
      <p className="cvv-prepare-gate__description" style={{ color: bodyCopy }}>
        {brandLabel
          ? t("transfer.prepare.cvvDescriptionWithBrand", {
              brand: brandLabel,
              last4,
            })
          : t("transfer.prepare.cvvDescription", { last4 })}
      </p>

      <div className="cvv-prepare-gate__field">
        <FloatingField
          label={t("card.cvvLabel")}
          placeholder={t("card.cvvFormatPlaceholder")}
          value={cvv}
          required
          colors={fieldColors}
        >
          <input
            type="password"
            inputMode="numeric"
            autoComplete="cc-csc"
            maxLength={4}
            style={{ color: bodyCopy }}
            value={cvv}
            disabled={loading}
            onChange={(e) =>
              setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
          />
        </FloatingField>
      </div>

      {error ? (
        <p className="cvv-prepare-gate__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="cvv-prepare-gate__actions">
        <Button
          type="submit"
          size="large"
          disabled={!valid || loading}
          background={backgroundBtn}
          color={textColorBtn}
        >
          {loading
            ? t("transfer.prepare.loading")
            : t("transfer.prepare.cvvSubmit")}
        </Button>
      </div>
    </form>
  );
};
